import { measureElement, useVirtualizer } from '@tanstack/react-virtual';
import head from 'lodash/head';
import isNil from 'lodash/isNil';
import last from 'lodash/last';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  type AriaRole,
  type CSSProperties,
  type ReactNode,
  type RefCallback,
} from 'react';

import { Div } from '@ui/components/base/div';
import { cn } from '@ui/lib/utils';

const DEFAULT_ESTIMATED_ITEM_SIZE = 36;
const OVERSCAN_COUNT = 8;

interface VirtualListRange {
  startIndex: number;
  endIndex: number;
}

interface VirtualListBaseProps {
  count: number;
  renderItem: (index: number) => ReactNode;
  getItemKey?: (index: number) => string | number;
  estimateSize?: number;
  initialScrollIndex?: number;
  className?: string;
  'aria-label'?: string;
  'aria-rowcount'?: number;
  role?: AriaRole;
  /** `top` keeps `position: sticky` working inside the items, which a transformed ancestor breaks. */
  positioning?: 'transform' | 'top';
  overflowX?: 'hidden' | 'auto';
  /** Width of the items, wider than the list when `overflowX` is `auto`. */
  contentWidth?: number;
  /** Rendered inside the scroll element, before the items: give it `sticky top-0` to keep it in view. */
  header?: ReactNode;
  /** Rendered inside the scroll element, after the items: give it `sticky bottom-0` to keep it in view. */
  footer?: ReactNode;
  itemsRole?: AriaRole;
  itemRole?: AriaRole;
  /** Called with the rendered range, overscan included; pass a stable callback. */
  onRangeChange?: (range: VirtualListRange) => void;
  onScroll?: () => void;
  scrollElementRef?: RefCallback<HTMLDivElement>;
}

interface VirtualListFillProps extends VirtualListBaseProps {
  sizing: 'fill';
  minVisibleItems?: number;
  maxHeight?: never;
}

interface VirtualListContentProps extends VirtualListBaseProps {
  sizing: 'content';
  maxHeight: number;
  minVisibleItems?: never;
}

type VirtualListProps = VirtualListFillProps | VirtualListContentProps;

interface VirtualListHandle {
  scrollToIndex: (index: number) => void;
}

const VirtualList = forwardRef<VirtualListHandle, VirtualListProps>(
  (
    {
      count,
      renderItem,
      getItemKey,
      estimateSize = DEFAULT_ESTIMATED_ITEM_SIZE,
      initialScrollIndex,
      sizing,
      minVisibleItems,
      maxHeight,
      className,
      'aria-label': ariaLabel,
      'aria-rowcount': ariaRowCount,
      role,
      positioning = 'transform',
      overflowX = 'hidden',
      contentWidth,
      header,
      footer,
      itemsRole,
      itemRole,
      onRangeChange,
      onScroll,
      scrollElementRef,
    },
    ref
  ) => {
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const hasAppliedInitialScroll = useRef<boolean>(false);
    const attachScrollElement = useCallback<RefCallback<HTMLDivElement>>(
      (node) => {
        scrollRef.current = node;
        scrollElementRef?.(node);
      },
      [scrollElementRef]
    );

    const virtualizer = useVirtualizer({
      count,
      getScrollElement: () => scrollRef.current,
      estimateSize: () => estimateSize,
      getItemKey,
      overscan: OVERSCAN_COUNT,
      measureElement: (element, entry, instance) => {
        const measuredSize = measureElement(element, entry, instance);
        return measuredSize > 0 ? measuredSize : estimateSize;
      },
    });

    useEffect(() => {
      if (hasAppliedInitialScroll.current || count === 0) {
        return;
      }
      hasAppliedInitialScroll.current = true;
      if (isNil(initialScrollIndex) || initialScrollIndex < 0 || initialScrollIndex >= count) {
        return;
      }
      virtualizer.scrollToIndex(initialScrollIndex, { align: 'center' });
    }, [count, initialScrollIndex, virtualizer]);

    useImperativeHandle(ref, () => ({ scrollToIndex: (index) => virtualizer.scrollToIndex(index) }), [virtualizer]);

    const virtualItems = virtualizer.getVirtualItems();
    const firstRenderedIndex = head(virtualItems)?.index;
    const lastRenderedIndex = last(virtualItems)?.index;

    useEffect(() => {
      if (isNil(onRangeChange) || isNil(firstRenderedIndex) || isNil(lastRenderedIndex)) {
        return;
      }
      onRangeChange({ endIndex: lastRenderedIndex, startIndex: firstRenderedIndex });
    }, [firstRenderedIndex, lastRenderedIndex, onRangeChange]);

    const fillMinHeight = isNil(minVisibleItems) ? undefined : minVisibleItems * estimateSize;
    const sizingStyle: CSSProperties = sizing === 'fill' ? { minHeight: fillMinHeight } : { maxHeight };

    return (
      <Div
        ref={attachScrollElement}
        data-slot="virtual-list"
        role={role}
        aria-label={ariaLabel}
        aria-rowcount={ariaRowCount}
        onScroll={onScroll}
        className={cn(
          'relative w-full',
          overflowX === 'auto' ? 'overflow-auto' : 'overflow-x-hidden overflow-y-auto',
          sizing === 'fill' && 'min-h-0 flex-1 basis-0',
          className
        )}
        style={sizingStyle}
      >
        {header}
        <Div
          role={itemsRole}
          className="relative w-full"
          style={{ height: virtualizer.getTotalSize(), width: contentWidth }}
        >
          {virtualItems.map((virtualItem) => (
            <Div
              key={virtualItem.key}
              ref={virtualizer.measureElement}
              role={itemRole}
              data-index={virtualItem.index}
              className={cn('absolute left-0 w-full', positioning === 'transform' && 'top-0')}
              style={
                positioning === 'top' ? { top: virtualItem.start } : { transform: `translateY(${virtualItem.start}px)` }
              }
            >
              {renderItem(virtualItem.index)}
            </Div>
          ))}
        </Div>
        {footer}
      </Div>
    );
  }
);
VirtualList.displayName = 'VirtualList';

export { VirtualList };
export type { VirtualListHandle, VirtualListProps, VirtualListRange };
